import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import uuidv4 from 'uuid/v4';
import {
  addFirestopItemToCreate,
  closeAddNewFirestopsDialog,
  closeConfirmFirestopItemDeleteDialog,
  closeConfirmFirestopListDeleteDialog,
  closeCreateFirestopListDialog,
  closeFirestopAdditionDialog,
  closeViewAndEditFirestopListDialog,
  editFirestopAttributes,
  modifyFirestopAdditionAttribute,
  modifyFirestopItemToCreate,
  modifyFirestopListAttribute,
  openAddNewFirestopDialog,
  openConfirmFirestopItemDeleteDialog,
  openConfirmFirestopListDeleteDialog,
  openCreateFirestopListDialog,
  openFirestopAdditionDialog,
  openFirestopList,
  removeFirestopImageDescription,
  setFirestopImageDescription,
  setFirestopImageToAdd,
  setIsCreatingOrUpdatingFirestopData,
  setIsPrintingFirestopListPdf,
} from './actions/firestop-documentation.actions';
import { createFirestopAddition } from './thunks/createFirestopAddition.thunk';
import { createFirestopList } from './thunks/createFirestopList.thunk';
import { createNewFirestops } from './thunks/createNewFirestops.thunk';
import { deleteFirestopItem } from './thunks/deleteFirestopItem.thunk';
import { deleteFirestopList } from './thunks/deleteFirestopList.thunk';
import { loadProjectFirestopDocumentationData } from './thunks/loadProjectFirestopDocumentationData.thunk';
import { saveFirestopListChanges } from './thunks/saveFirestopListChanges.thunk';

const firestopListsAdapter = createEntityAdapter();
const firestopItemsAdapter = createEntityAdapter();

const firestopImagesAdapter = createEntityAdapter();
// const defectFixesAdapter = createEntityAdapter();

const firestopDocumentationSlice = createSlice({
  name: 'firestopDocumentation',
  initialState: {
    firestopLists: firestopListsAdapter.getInitialState(),
    firestopItems: firestopItemsAdapter.getInitialState(),
    firestopImages: firestopImagesAdapter.getInitialState(),
    changeHistoryItemsByItemId: {},
    isCreateFirestopListDialogOpen: false,
    isAddNewFirestopDialogOpen: false,
    isEditingListAttributes: false,
    loading: false,
    isCreatingOrUpdatingFirestopData: false,
    isPrintingFirestopListPdf: false,
    firestopListAttributeModifications: {},
    firestopItemsToCreate: {},
    editedFirestopAttributes: {},
    firestopImagesToAdd: [],
    defectAssignmentModifications: {},
    defectEditAssignmentsToAdd: {},
    defectEditRemovedAssignations: {},
    defectEditImagesToDelete: [],
    defectFixAttributes: {},
    firestopAdditionAttributes: {},
    selectedFirestopListId: '',
    isConfirmDefectDeleteDialogOpen: false,
    isConfirmDeleteFirestopListDialogOpen: false,
    isDeletingFirestopList: false,
    selectedFirestopItemIdToDelete: '',
    selectedDefectItemIdToEdit: '',
    selectedDefectItemIdToFix: '',
    selectedAdditionFirestopItemId: '',
    selectedDefectItemIdToReassign: '',
    imageDescriptions: {},
    latestWorkedAtByResource: {},
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(
        loadProjectFirestopDocumentationData.pending,
        (state, action) => {
          state.loading = true;
        }
      )
      .addCase(
        loadProjectFirestopDocumentationData.fulfilled,
        (state, action) => {
          const { firestopLists, firestopItems, firestopImages } =
            action.payload;
          firestopListsAdapter.setMany(state.firestopLists, firestopLists);
          firestopItemsAdapter.setMany(state.firestopItems, firestopItems);
          firestopImagesAdapter.setMany(state.firestopImages, firestopImages);

          state.loading = false;
        }
      )
      .addCase(addFirestopItemToCreate, (state, action) => {
        const newInitialFirestopItem = { id: uuidv4() };

        state.defectItemsToCreate[newInitialFirestopItem.id] =
          newInitialFirestopItem;
      })
      .addCase(createFirestopList.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createFirestopList.fulfilled, (state, action) => {
        const { firestopList } = action.payload;
        firestopListsAdapter.setOne(state.firestopLists, firestopList);

        state.selectedFirestopListId = firestopList.id;

        state.isCreateFirestopListDialogOpen = false;
        state.isCreatingOrUpdatingFirestopData = false;
      })
      .addCase(createFirestopList.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkodokumentaation luonnissa', {
          variant: 'error',
        });
      })
      .addCase(createNewFirestops.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createNewFirestops.fulfilled, (state, action) => {
        const { firestopItems, firestopAssignments, addedFirestopImages } =
          action.payload;
        firestopItemsAdapter.setMany(state.firestopItems, firestopItems);

        firestopImagesAdapter.setMany(
          state.firestopImages,
          addedFirestopImages
        );

        state.isCreatingOrUpdatingFirestopData = false;

        enqueueSnackbar('Palokatko luotu', {
          variant: 'success',
        });

        state.firestopImagesToAdd = [];
        state.imageDescriptions = {};
      })
      .addCase(createNewFirestops.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
      })
      .addCase(openCreateFirestopListDialog, (state, action) => {
        const id = uuidv4();
        state.isCreateFirestopListDialogOpen = true;
        state.firestopItemsToCreate[id] = { id };
      })
      .addCase(closeCreateFirestopListDialog, (state, action) => {
        state.isCreateFirestopListDialogOpen = false;
        state.firestopItemsToCreate = {};
        state.imageDescriptions = {};
      })
      .addCase(modifyFirestopItemToCreate, (state, action) => {
        const { id, attribute, value } = action.payload;

        state.firestopItemsToCreate[id] = {
          ...state.firestopItemsToCreate[id],
          [attribute]: value,
        };
      })
      .addCase(editFirestopAttributes, (state, action) => {
        const { attribute, value } = action.payload;

        state.editedFirestopAttributes[attribute] = value;
      })
      .addCase(openFirestopList, (state, action) => {
        state.selectedFirestopListId = action.payload;
      })
      // .addCase(defectListActions.setDefectAssignation, (state, action) => {
      //   const { defectId } = action.payload;

      //   if (!state.defectAssignmentModifications[defectId]) {
      //     state.defectAssignmentModifications[defectId] = [action.payload];
      //   } else {
      //     state.defectAssignmentModifications[defectId].push(action.payload);
      //   }
      // })
      // .addCase(
      //   defectListActions.setMultipleDefectAssignation,
      //   (state, action) => {
      //     const assignations = action.payload;

      //     assignations.forEach((assignment) => {
      //       if (!state.defectAssignmentModifications[assignment.defectId]) {
      //         state.defectAssignmentModifications[assignment.defectId] = [
      //           assignment,
      //         ];
      //       } else {
      //         state.defectAssignmentModifications[assignment.defectId].push(
      //           assignment
      //         );
      //       }
      //     });
      //   }
      // )
      // .addCase(defectListActions.removeDefectAssignation, (state, action) => {
      //   const { userId, businessId, defectId } = action.payload;

      //   if (!userId) {
      //     state.defectAssignmentModifications[defectId] =
      //       state.defectAssignmentModifications[defectId].filter(
      //         (assignment) => assignment.businessId !== businessId
      //       );
      //   } else {
      //     state.defectAssignmentModifications[defectId] =
      //       state.defectAssignmentModifications[defectId].filter(
      //         (assignment) =>
      //           assignment.businessId !== businessId ||
      //           (assignment.businessId === businessId &&
      //             assignment.userId !== userId)
      //       );
      //   }
      // })
      // .addCase(
      //   defectListActions.removeDefectEditAssignation,
      //   (state, action) => {
      //     const { businessId, userId } = action.payload;

      //     if (!userId) {
      //       const businessAssignments = Object.values(
      //         state.defectEditAssignmentsToAdd
      //       ).filter((assignment) => assignment.businessId === businessId);

      //       businessAssignments.forEach((assignment) => {
      //         delete state.defectEditAssignmentsToAdd[
      //           [`${assignment.businessId}#${assignment.userId}`]
      //         ];
      //       });
      //     } else {
      //       delete state.defectEditAssignmentsToAdd[
      //         [`${businessId}#${userId}`]
      //       ];
      //     }

      //     state.defectEditRemovedAssignations[`${businessId}#${userId}`] =
      //       action.payload;
      //   }
      // )
      // .addCase(defectListActions.setDefectEditAssignation, (state, action) => {
      //   const { businessId, userId } = action.payload;

      //   delete state.defectEditRemovedAssignations[`${businessId}#${userId}`];

      //   state.defectEditAssignmentsToAdd[`${businessId}#${userId}`] =
      //     action.payload;
      // })
      // .addCase(loadAllProjectsUsersByCompany.fulfilled, (state, action) => {
      //   state.projectUsersByCompany = action.payload;
      // })
      .addCase(closeViewAndEditFirestopListDialog, (state, action) => {
        state.selectedFirestopListId = '';
        state.firestopListAttributeModifications = {};
        state.isEditingListAttributes = false;
      })
      // .addCase(defectListActions.toggleEditListAttributes, (state, action) => {
      //   const toggleValue = action.payload;

      //   state.isEditingListAttributes = toggleValue;
      //   state.defectListAttributeModifications = {};
      // })
      .addCase(modifyFirestopListAttribute, (state, action) => {
        state.firestopListAttributeModifications = {
          ...state.firestopListAttributeModifications,
          ...action.payload,
        };
      })
      .addCase(saveFirestopListChanges.fulfilled, (state, action) => {
        const { updatedFirestopListItem } = action.payload;

        firestopListsAdapter.upsertOne(
          state.firestopLists,
          updatedFirestopListItem
        );
        state.firestopListAttributeModifications = {};
        state.isEditingListAttributes = false;
      })
      .addCase(openAddNewFirestopDialog, (state, action) => {
        const id = uuidv4();
        state.isAddNewFirestopDialogOpen = true;
        state.firestopItemsToCreate[id] = { id };
      })
      .addCase(closeAddNewFirestopsDialog, (state, action) => {
        state.isAddNewFirestopDialogOpen = false;
        state.firestopItemsToCreate = {};
        state.imageDescriptions = {};
      })
      .addCase(openConfirmFirestopItemDeleteDialog, (state, action) => {
        state.selectedFirestopItemIdToDelete = action.payload;
      })
      .addCase(closeConfirmFirestopItemDeleteDialog, (state, action) => {
        state.selectedFirestopItemIdToDelete = '';
      })
      .addCase(deleteFirestopItem.fulfilled, (state, action) => {
        firestopItemsAdapter.removeOne(state.firestopItems, action.payload.id);
        state.selectedFirestopItemIdToDelete = '';
      })
      // .addCase(defectListActions.openEditDefectItemDialog, (state, action) => {
      //   state.selectedDefectItemIdToEdit = action.payload;
      // })
      // .addCase(defectListActions.closeEditDefectItemDialog, (state, action) => {
      //   state.selectedDefectItemIdToEdit = '';
      //   state.editedFirestopAttributes = {};
      //   state.defectEditAssignmentsToAdd = {};
      //   state.defectEditRemovedAssignations = {};
      //   state.defectImagesToAdd = [];
      //   state.defectEditImagesToDelete = [];
      //   state.imageDescriptions = {};
      // })
      // .addCase(updateDefectItem.pending, (state, action) => {
      //   state.isCreatingOrUpdatingDefectData = true;
      // })
      // .addCase(updateDefectItem.fulfilled, (state, action) => {
      //   const {
      //     updatedDefectItem,
      //     createdAssignments,
      //     removedAssignments,
      //     addedDefectImages,
      //     deletedDefectImages,
      //   } = action.payload;

      //   defectItemsAdapter.setOne(state.defectItems, updatedDefectItem);
      //   defectAssignmentsAdapter.setMany(
      //     state.defectAssignments,
      //     createdAssignments
      //   );

      //   defectImagesAdapter.setMany(state.defectImages, addedDefectImages);

      //   if (removedAssignments && removedAssignments.length > 0) {
      //     const removedIds = removedAssignments.map(
      //       (assignment) => `${assignment.defectId}#${assignment.assigneeId}`
      //     );
      //     defectAssignmentsAdapter.removeMany(
      //       state.defectAssignments,
      //       removedIds
      //     );
      //   }

      //   if (deletedDefectImages && deletedDefectImages.length > 0) {
      //     const removedIds = deletedDefectImages.map(
      //       (defectImage) => defectImage.id
      //     );
      //     defectImagesAdapter.removeMany(state.defectImages, removedIds);
      //   }
      //   state.defectImagesToAdd = [];
      //   state.selectedDefectItemIdToEdit = '';
      //   state.defectEditImagesToDelete = [];
      //   state.editedFirestopAttributes = {};
      //   state.isCreatingOrUpdatingDefectData = false;
      //   state.imageDescriptions = {};

      //   enqueueSnackbar('ViPu päivitetty onnistuneesti', {
      //     variant: 'success',
      //   });
      // })
      // .addCase(reassignDefectItem.fulfilled, (state, action) => {
      //   const { createdAssignments, removedAssignments } = action.payload;

      //   defectAssignmentsAdapter.setMany(
      //     state.defectAssignments,
      //     createdAssignments
      //   );

      //   if (removedAssignments && removedAssignments.length > 0) {
      //     const removedIds = removedAssignments.map(
      //       (assignment) => `${assignment.defectId}#${assignment.assigneeId}`
      //     );
      //     defectAssignmentsAdapter.removeMany(
      //       state.defectAssignments,
      //       removedIds
      //     );
      //   }
      //   state.defectAssignmentModifications = {};
      //   state.selectedDefectItemIdToReassign = '';
      //   state.isCreatingOrUpdatingDefectData = false;

      //   enqueueSnackbar('Urakoitsija päivitetty onnistuneesti', {
      //     variant: 'success',
      //   });
      // })
      // .addCase(updateDefectItem.rejected, (state, action) => {
      //   state.isCreatingOrUpdatingDefectData = false;
      // })
      .addCase(setFirestopImageToAdd, (state, action) => {
        state.firestopImagesToAdd.push(action.payload);
      })
      // .addCase(defectListActions.setDefectImageToDelete, (state, action) => {
      //   state.defectEditImagesToDelete.push(action.payload);
      // })
      // .addCase(defectListActions.openDefectItemFixDialog, (state, action) => {
      //   state.selectedDefectItemIdToFix = action.payload;
      // })
      // .addCase(defectListActions.closeDefectItemFixDialog, (state, action) => {
      //   state.selectedDefectItemIdToFix = '';
      //   state.defectFixAttributes = {};
      //   state.imageDescriptions = {};
      // })
      .addCase(modifyFirestopAdditionAttribute, (state, action) => {
        const { attribute, value } = action.payload;

        state.firestopAdditionAttributes = {
          ...state.firestopAdditionAttributes,
          [attribute]: value,
        };
      })
      // .addCase(createDefectFix.fulfilled, (state, action) => {
      //   const { defectFix, defectItem, addedDefectImages } = action.payload;

      //   defectFixesAdapter.setOne(state.defectFixes, defectFix);
      //   defectItemsAdapter.setOne(state.defectItems, defectItem);
      //   defectImagesAdapter.setMany(state.defectImages, addedDefectImages);

      //   state.selectedDefectItemIdToFix = '';
      //   state.defectFixAttributes = {};
      //   state.isCreatingOrUpdatingDefectData = false;
      //   state.imageDescriptions = {};
      //   state.defectImagesToAdd = [];

      //   enqueueSnackbar('ViPu:n korjaus lisätty onnistuneesti', {
      //     variant: 'success',
      //   });
      // })
      // .addCase(createDefectFix.rejected, (state, action) => {
      //   state.isCreatingOrUpdatingDefectData = false;
      // })
      .addCase(createFirestopAddition.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createFirestopAddition.fulfilled, (state, action) => {
        const { firestopItem, addedFirestopImages } = action.payload;

        firestopItemsAdapter.setOne(state.firestopItems, firestopItem);
        firestopImagesAdapter.setMany(
          state.firestopImages,
          addedFirestopImages
        );

        state.selectedAdditionFirestopItemId = '';
        state.firestopAdditionAttributes = {};
        state.isCreatingOrUpdatingFirestopData = false;
        state.imageDescriptions = {};
        state.firestopImagesToAdd = [];

        enqueueSnackbar('Palokatko päivitetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(createFirestopAddition.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkon päivityksessä', {
          variant: 'error',
        });
      })
      .addCase(setIsCreatingOrUpdatingFirestopData, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = action.payload;
      })
      .addCase(setFirestopImageDescription, (state, action) => {
        const { firestopItemId, description, index } = action.payload;

        const currentFirestopImageDescriptions =
          state.imageDescriptions[firestopItemId];

        state.imageDescriptions = {
          ...state.imageDescriptions,
          [firestopItemId]: {
            ...currentFirestopImageDescriptions,
            [index]: description,
          },
        };
      })
      .addCase(removeFirestopImageDescription, (state, action) => {
        const { firestopItemId, index } = action.payload;
        const currentFirestopImagesDescriptions =
          state.imageDescriptions[firestopItemId];

        if (currentFirestopImagesDescriptions) {
          delete currentFirestopImagesDescriptions[index];
        }
      })
      .addCase(openFirestopAdditionDialog, (state, action) => {
        state.selectedAdditionFirestopItemId = action.payload;
      })

      .addCase(closeFirestopAdditionDialog, (state, action) => {
        state.selectedAdditionFirestopItemId = '';
      })
      // .addCase(
      //   defectListActions.openDefectAssigneeSwitchDialog,
      //   (state, action) => {
      //     state.selectedDefectItemIdToReassign = action.payload;
      //   }
      // )
      // .addCase(
      //   defectListActions.closeDefectAssigneeSwitchDialog,
      //   (state, action) => {
      //     state.selectedDefectItemIdToReassign = '';
      //   }
      // )

      .addCase(setIsPrintingFirestopListPdf, (state, action) => {
        state.isPrintingFirestopListPdf = action.payload;
      })
      .addCase(deleteFirestopList.pending, (state, action) => {
        state.isDeletingFirestopList = true;
      })
      .addCase(deleteFirestopList.fulfilled, (state, action) => {
        const {
          deletedFirestopItems,
          deletedFirestopFixes,
          deletedFirestopImages,
          deletedFirestopListItem,
        } = action.payload;

        if (deletedFirestopListItem) {
          firestopListsAdapter.upsertOne(
            state.firestopLists,
            deletedFirestopListItem
          );
        }

        if (deletedFirestopItems && deletedFirestopItems.length > 0) {
          firestopItemsAdapter.upsertMany(
            state.firestopItems,
            deletedFirestopItems
          );
        }
        // if (deletedDefectFixes && deletedDefectFixes.length > 0) {
        //   defectFixesAdapter.upsertMany(state.defectFixes, deletedDefectFixes);
        // }
        if (deletedFirestopImages && deletedFirestopImages.length > 0) {
          firestopImagesAdapter.upsertMany(
            state.firestopImages,
            deletedFirestopImages
          );
        }

        state.isConfirmDeleteFirestopListDialogOpen = false;
        state.selectedFirestopListId = '';
        state.isDeletingFirestopList = false;

        enqueueSnackbar('Palokatkodokumentaatio poistettu onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(deleteFirestopList.rejected, (state, action) => {
        state.isDeletingFirestopList = false;
        enqueueSnackbar('Virhe palokatkodokumentaation poistossa', {
          variant: 'error',
        });
      })
      .addCase(openConfirmFirestopListDeleteDialog, (state, action) => {
        state.isConfirmDeleteFirestopListDialogOpen = true;
      })
      .addCase(closeConfirmFirestopListDeleteDialog, (state, action) => {
        state.isConfirmDeleteFirestopListDialogOpen = false;
      });
  },
});

export default firestopDocumentationSlice.reducer;
